import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { isFirefox } from '../utils'
import { useShallowSelector, useWorkspace2Store } from '../Workspace2Store'

interface CKEditorToggleResult<T> {
    /** A flag indicating whether a CKEditor instance is currently active. */
    active: boolean
    /** The key of the currently active CKEditor instance, or null if none is active. */
    activeEditorKey: T | null
    /** A reference to the CKEditor instance. */
    placeholderRef: RefObject<HTMLDivElement>
    /** A function to activate a CKEditor instance by key. */
    activate: (key?: T) => void
    /** A function to deactivate the currently active CKEditor instance. */
    deactivate: () => void
    /** A function to mark CKEditor mount as complete, allowing deactivation. */
    markMountComplete: () => void
}

/**
 * A hook to manage CKEditor activation and deactivation.
 *
 * Features:
 * - Activates CKEditor when clicking on a placeholder.
 * - Deactivates CKEditor when clicking outside or losing focus (e.g., pressing Tab).
 * - Ensures only one editor is active at a time.
 * - Automatically deactivates all CKEditor instances when contentItemViewNodes changes in the workspace.
 *
 * @template T - The type of the key used to identify active CKEditor instances.
 * @param {T | null} contentItemId - The key/id of the content item associated with the CKEditor.
 * @returns {CKEditorToggleResult<T>} An object containing the state and functions for managing CKEditor activation and deactivation.
 */
export const useCKEditorToggle = <T>(
    contentItemId: T | null
): CKEditorToggleResult<T> => {
    const [activeEditorKey, setActiveEditorKey] = useState<T | null>(null)
    const placeholderRef = useRef<HTMLDivElement>(null)
    // Track if CKEditor is currently mounting to prevent spurious blur events from deactivating
    const isMountingRef = useRef(false)
    // Track if mount is complete - isMountingRef will be cleared on next user interaction
    const mountCompleteRef = useRef(false)

    // subscribe to the global editor deactivation signal
    const { editorDeactivationSignal } = useShallowSelector((s) => ({
        editorDeactivationSignal: s.editorDeactivationSignal,
    }))

    const active = activeEditorKey === contentItemId

    // handler that Activates CKEditor using its key/id
    const activate = useCallback(
        (currentContentId?: T) => {
            // Only mark as mounting if not already active
            // This prevents re-activation from resetting isMountingRef after markMountComplete was called
            if (!active) {
                isMountingRef.current = true
                mountCompleteRef.current = false
            }
            setActiveEditorKey(currentContentId ?? contentItemId)
        },
        [active, contentItemId]
    )

    // Called by onConfigComplete after ContentEditor's configureEditor() has actually run.
    // (state-based signal.instead of timing-based)
    const markMountComplete = useCallback(() => {
        mountCompleteRef.current = true
        isMountingRef.current = false
    }, [])

    // handler that Deactivates CKEditor when clicking outside or losing focus
    const deactivate = useCallback(() => {
        // Don't deactivate while CKEditor is mounting (mount causes spurious blur events)
        if (isMountingRef.current) {
            return
        }
        // Note: We removed the ckEditorHasFocus check because on mousedown events,
        // focus hasn't moved yet so it incorrectly blocked deactivation.
        // The handleBlurOrClickOutside already checks if click is inside CKEditor.
        setActiveEditorKey(null)
    }, [contentItemId, active])

    // deactivate all instance of CKEditor when global deactivation signal changes
    // But don't deactivate if this is the currently active editor (prevents self-deactivation)
    // Also don't deactivate if currently mounting (prevents race condition where newly activating
    // element gets deactivated before state update completes)
    useEffect(() => {
        if (editorDeactivationSignal > 0 && !active && !isMountingRef.current) {
            deactivate()
        }
    }, [editorDeactivationSignal, deactivate, active])

    /**
     * Handle Firefox-specific text selection behavior
     * when rapidly clicking between multiple pills. Now uses synchronous
     * check with a microtask to ensure selection state is available.
     */
    const handleFirefoxClick = useCallback(
        (target: HTMLElement) => {
            // Clear isMountingRef on user click if mount is complete
            if (mountCompleteRef.current && isMountingRef.current) {
                isMountingRef.current = false
            }

            // Check if click is outside our placeholder
            const isOutsideClick =
                placeholderRef.current &&
                !placeholderRef.current.contains(target)

            if (!isOutsideClick) {
                return
            }

            // Use microtask (Promise.resolve) instead of setTimeout
            // This runs after current event but before next render,
            // allowing selection state to be available without the 50ms delay
            // that was causing race conditions
            Promise.resolve()
                .then(() => {
                    // Only deactivate if there's no text selection
                    const selection = window.getSelection()
                    const hasSelection =
                        selection && selection.toString().length > 0

                    if (!hasSelection && placeholderRef.current) {
                        deactivate()
                    }
                })
                .catch(() => {
                    // Silent error handling - fallback deactivation
                    try {
                        if (placeholderRef.current) {
                            deactivate()
                        }
                    } catch {
                        // Silent catch
                    }
                })
        },
        [deactivate]
    )

    /** registers event listeners to handle outside clicks and loss of focus */
    useEffect(() => {
        const controller = new AbortController()
        const signal = controller.signal

        const handleBlurOrClickOutside = (event: Event) => {
            const target = event.target as HTMLElement
            const isUserClick =
                event.type === 'mousedown' || event.type === 'click'

            // On real user click events, clear isMountingRef if mount is complete
            // This allows deactivation to proceed for subsequent blur/click events
            if (
                isUserClick &&
                mountCompleteRef.current &&
                isMountingRef.current
            ) {
                isMountingRef.current = false
            }

            // Skip deactivation when an action bar button is being pressed (mousedown
            // sets the flag; focusin from the same press fires before click, so we
            // guard all event types here, not just mousedown/click).
            if (useWorkspace2Store.getState().isActionButtonMousedown) {
                return
            }

            // skip deactivation if the event is from CKEditor
            if (
                target?.closest('.ck-editor') ||
                target?.closest('.ck-balloon-panel')
            ) {
                return
            }

            if (isFirefox && event.type === 'click') {
                handleFirefoxClick(target)
                return
            }

            const isOutside =
                placeholderRef.current &&
                !placeholderRef.current.contains(event.target as Node)

            if (isOutside) {
                deactivate()
            }
        }

        if (activeEditorKey) {
            const eventType = isFirefox ? 'click' : 'mousedown'
            document.addEventListener(eventType, handleBlurOrClickOutside, {
                signal,
            })
            document.addEventListener('focusin', handleBlurOrClickOutside, {
                signal,
            })
        } else {
            controller.abort()
        }

        return () => controller.abort()
    }, [activeEditorKey, deactivate, handleFirefoxClick])

    return {
        active,
        activeEditorKey,
        placeholderRef,
        activate,
        deactivate,
        markMountComplete,
    }
}
